import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Pill, fmtBRL } from "@atlas/ui/web";
import type { ContratoPortavel, Portabilidade } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { MatriculaInfo, readActiveMatricula, hydrateMatriculas, STORAGE_KEY_ID, STORAGE_KEY_META } from "../../lib/matricula-data";
import { fmtDataHora } from "../../lib/refinanciamento";
import {
  ESTADO_PORTABILIDADE_SERVIDOR,
  acoesDoServidorNaPortabilidade,
  pillDaPortabilidade,
  portabilidadeEmAberto,
  prazoDaEtapa,
} from "../../lib/portabilidade";
import { ImpersonateReadOnlyNotice, useIsImpersonating } from "../../components/ImpersonateBar";

const inputStyle: CSSProperties = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid var(--border-strong)",
  background: "var(--bg-elev-2)",
  color: "var(--text)",
  fontSize: 14,
  fontFamily: "inherit",
  boxSizing: "border-box",
  width: "100%",
};

/**
 * Portabilidade: levar um contrato para outro banco. O servidor escolhe o
 * contrato e o banco de destino (com a senha de transacao), ou confirma o
 * pedido que um banco abriu. Dali o rito anda entre os bancos: o atual informa
 * o saldo, o novo informa as condicoes, o atual quita — e o contrato novo ja'
 * nasce averbado.
 */
export function ServidorPortabilidade() {
  const [sp] = useSearchParams();
  const bancoSugerido = (sp.get("banco") ?? "").toLowerCase();
  const qc = useQueryClient();
  const impersonando = useIsImpersonating();
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());
  const [exigeSenha, setExigeSenha] = useState(() => readActiveMatricula()?.exigeSenhaTransacao === true);

  useEffect(() => {
    void hydrateMatriculas().then(() => {
      setInfo(readActiveMatricula());
      setExigeSenha(readActiveMatricula()?.exigeSenhaTransacao === true);
    });
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) setInfo(readActiveMatricula());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const matricula = info?.matricula;
  const minhasQ = useQuery({
    queryKey: ["servidor", "portabilidade"],
    queryFn: () => atlas.portabilidade.minhas(),
    refetchInterval: 10_000,
    refetchOnMount: "always",
  });
  const elegiveisQ = useQuery({
    queryKey: ["servidor", "portabilidade", "elegiveis", matricula],
    queryFn: () => atlas.portabilidade.elegiveis(matricula),
    enabled: !!matricula,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ["servidor", "portabilidade"] });
    void qc.invalidateQueries({ queryKey: ["servidor", "propostas"] });
    void hydrateMatriculas().then(() => setInfo(readActiveMatricula()));
  };

  const minhas = minhasQ.data?.itens ?? [];
  const abertas = minhas.filter(portabilidadeEmAberto);
  const encerradas = minhas.filter((p) => !portabilidadeEmAberto(p));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span className="eyebrow">Portabilidade</span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Levar um contrato para outra instituição</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 6, maxWidth: 720, lineHeight: 1.5 }}>
          Escolha o contrato e a instituição que vai recebê-lo. A instituição atual informa o saldo devedor, a nova informa as
          condições, e a atual quita — o contrato novo já entra averbado e o antigo sai da folha. Para renegociar com a
          mesma instituição, use o <Link to="/servidor/refinanciamento" style={{ color: "var(--gold-500)" }}>refinanciamento</Link>.
        </p>
      </header>

      {minhasQ.error ? (
        <p style={{ color: "var(--danger, #DC2626)" }}>{mensagemDeErro(minhasQ.error, "Não foi possível carregar suas portabilidades.")}</p>
      ) : null}

      {abertas.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Em andamento</h2>
          {abertas.map((p) => (
            <PortabilidadeCard key={p.id} p={p} exigeSenha={exigeSenha} impersonando={impersonando} onMudou={invalidar} />
          ))}
        </section>
      ) : null}

      <NovoPedido
        key={matricula ?? "-"}
        matricula={matricula}
        contratos={elegiveisQ.data?.contratos ?? []}
        bancos={elegiveisQ.data?.bancosDestino ?? []}
        bancoSugerido={bancoSugerido}
        carregando={elegiveisQ.isLoading}
        erro={elegiveisQ.error ? mensagemDeErro(elegiveisQ.error, "Não foi possível carregar seus contratos.") : null}
        exigeSenha={exigeSenha}
        impersonando={impersonando}
        onCriado={invalidar}
      />

      {encerradas.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Histórico</h2>
          {encerradas.map((p) => (
            <PortabilidadeCard key={p.id} p={p} exigeSenha={exigeSenha} impersonando={impersonando} onMudou={invalidar} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function NovoPedido({
  matricula,
  contratos,
  bancos,
  bancoSugerido,
  carregando,
  erro,
  exigeSenha,
  impersonando,
  onCriado,
}: {
  matricula: string | undefined;
  contratos: ContratoPortavel[];
  bancos: { bancoId: number; bancoNome: string; convenioId: string }[];
  bancoSugerido: string;
  carregando: boolean;
  erro: string | null;
  exigeSenha: boolean;
  impersonando: boolean;
  onCriado: () => void;
}) {
  const [adf, setAdf] = useState<string | null>(null);
  const [bancoId, setBancoId] = useState<number | null>(null);
  const [senha, setSenha] = useState("");
  const [ciente, setCiente] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  const escolhido = contratos.find((c) => c.adf === adf);
  // A credora do contrato nao e' destino: isso e' refinanciamento.
  const destinos = bancos.filter((b) => !escolhido || b.bancoId !== escolhido.credora.id);

  useEffect(() => {
    if (!bancoSugerido || bancoId != null) return;
    const b = destinos.find((d) => d.bancoNome.toLowerCase() === bancoSugerido);
    if (b) setBancoId(b.bancoId);
  }, [bancoSugerido, destinos, bancoId]);
  useEffect(() => {
    if (bancoId != null && !destinos.some((d) => d.bancoId === bancoId)) setBancoId(null);
  }, [destinos, bancoId]);

  const pronto = !!escolhido && bancoId != null && ciente && (!exigeSenha || senha.length > 0);
  const mut = useMutation({
    mutationFn: () => atlas.portabilidade.pedir({ adf: adf!, bancoDestinoId: bancoId!, matricula, senha: exigeSenha ? senha : undefined }),
    onSuccess: (p) => {
      setOk(`Portabilidade ${p.id} pedida. Aguarde ${p.proponente.nome} apresentar a proposta para você conferir e aceitar.`);
      setAdf(null);
      setBancoId(null);
      setSenha("");
      setCiente(false);
      onCriado();
    },
  });

  return (
    <Card>
      <h2 style={{ margin: "0 0 4px", fontSize: "1.1rem" }}>Pedir portabilidade</h2>
      <p style={{ margin: "0 0 14px", color: "var(--text-muted)", fontSize: ".9rem" }}>
        Só entram empréstimos ativos em folha. A instituição de destino precisa ter convênio com o seu órgão ou entidade.
      </p>
      {ok ? <div role="status" style={{ marginBottom: 12, color: "var(--emerald-500)", fontWeight: 600 }}>{ok}</div> : null}
      {erro ? <p style={{ color: "var(--danger, #DC2626)" }}>{erro}</p> : null}
      {carregando ? <p style={{ color: "var(--text-muted)" }}>Carregando seus contratos…</p> : null}
      {!carregando && !erro && contratos.length === 0 ? (
        <p style={{ color: "var(--text-muted)", margin: 0 }}>Você não tem empréstimos ativos para portar agora.</p>
      ) : null}

      <div style={{ display: "grid", gap: 10 }}>
        {contratos.map((c) => {
          const marcado = adf === c.adf;
          return (
            <label
              key={c.adf}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                padding: 12,
                borderRadius: 10,
                border: `1px solid ${marcado ? "var(--gold-500)" : "var(--border)"}`,
                cursor: c.elegivel ? "pointer" : "not-allowed",
                opacity: c.elegivel ? 1 : 0.6,
              }}
            >
              <input type="radio" name="contrato" checked={marcado} disabled={!c.elegivel} onChange={() => setAdf(c.adf)} style={{ marginTop: 4 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{c.credora.nome}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      {c.adf} · {c.convenioNome}
                    </div>
                  </div>
                  <Pill variant={c.elegivel ? "emdia" : "expirado"}>{c.elegivel ? "Pode portar" : "Indisponível"}</Pill>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 10, fontSize: 13 }}>
                  <KV label="Parcela" v={fmtBRL(c.valorParcela)} />
                  <KV label="Saldo devedor" v={fmtBRL(c.saldoDevedor)} />
                  <KV label="Pagas" v={`${c.parcelasPagas}/${c.totalParcelas}`} />
                </div>
                {c.motivo ? <div style={{ marginTop: 8, fontSize: 12, color: "var(--gold-500)" }}>{c.motivo}</div> : null}
              </div>
            </label>
          );
        })}
      </div>

      {escolhido ? (
        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          <Campo label="Instituição de destino">
            <select style={inputStyle} value={bancoId ?? ""} onChange={(e) => setBancoId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Escolha a instituição</option>
              {destinos.map((b) => (
                <option key={b.bancoId} value={b.bancoId}>{b.bancoNome}</option>
              ))}
            </select>
          </Campo>
          {destinos.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--gold-500)" }}>Nenhuma outra instituição tem convênio com o seu órgão ou entidade.</p>
          ) : null}
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: ".9rem" }}>
            <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} disabled={impersonando} />
            Autorizo a portabilidade do contrato {escolhido.adf} para a instituição escolhida, nos termos de portabilidade.
          </label>
          {exigeSenha ? (
            <Campo label="Senha de transação">
              <input style={{ ...inputStyle, maxWidth: 280 }} type="password" autoComplete="one-time-code" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Senha de transação" />
            </Campo>
          ) : null}
          {mut.error ? (
            <div role="alert" style={{ color: "var(--danger, #DC2626)", fontSize: ".9rem" }}>{mensagemDeErro(mut.error, "Não foi possível pedir a portabilidade.")}</div>
          ) : null}
          <ImpersonateReadOnlyNotice acao="Pedir portabilidade" />
          <div>
            <Button onClick={() => { setOk(null); mut.mutate(); }} disabled={!pronto || mut.isPending || impersonando}>
              {mut.isPending ? "Enviando…" : "Pedir portabilidade"}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function PortabilidadeCard({
  p,
  exigeSenha,
  impersonando,
  onMudou,
}: {
  p: Portabilidade;
  exigeSenha: boolean;
  impersonando: boolean;
  onMudou: () => void;
}) {
  const acoes = acoesDoServidorNaPortabilidade(p);
  const [senha, setSenha] = useState("");
  const [ciente, setCiente] = useState(false);
  const confirmar = useMutation({ mutationFn: () => atlas.portabilidade.confirmar(p.id, exigeSenha ? senha : undefined, p.versao), onSuccess: onMudou });
  const cancelar = useMutation({ mutationFn: () => atlas.portabilidade.cancelarComoServidor(p.id), onSuccess: onMudou });
  const erro = confirmar.error ?? cancelar.error;
  const ocupado = confirmar.isPending || cancelar.isPending;
  const c = p.condicoes;
  const prazo = prazoDaEtapa(p.expiraEm);

  return (
    <Card style={{ borderColor: acoes.confirmar ? "var(--gold-500)" : undefined }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700 }}>
            {p.credora.nome} → {p.proponente.nome}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {p.id} · {p.solicitadoPor === "servidor" ? "seu pedido" : `aberta por ${p.proponente.nome}`} · {fmtDataHora(p.criadoEm)}
          </div>
        </div>
        <Pill variant={pillDaPortabilidade(p.estado)}>{p.analiseManual && p.estado === "aguardando_liquidacao" ? "Comprovante em análise pela averbadora" : p.fluxoLegado && p.estado === "aguardando_servidor" ? "Aguardando a sua confirmação" : ESTADO_PORTABILIDADE_SERVIDOR[p.estado]}</Pill>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 12, fontSize: 13 }}>
        <KV label="Contrato atual" v={p.origem.adf} />
        <KV label="Parcela atual" v={fmtBRL(p.origem.parcela)} />
        <KV label="Saldo informado" v={p.saldoInformado == null ? "—" : fmtBRL(p.saldoInformado)} />
        {c ? <KV label="Valor financiado" v={fmtBRL(c.valorFinanciado)} /> : null}
        {c ? <KV label="CET mensal" v={`${(c.cetAm * 100).toFixed(2).replace(".", ",")}% a.m.`} /> : null}
        {c?.taxaAm != null ? <KV label="Juros mensais" v={`${(c.taxaAm * 100).toFixed(2).replace(".", ",")}% a.m.`} /> : null}
        {c ? <KV label="Nova parcela" v={`${c.prazo}x de ${fmtBRL(c.valorParcela)}`} accent /> : null}
        {c ? <KV label="Liberado na conta" v={fmtBRL(c.valorLiquido)} accent /> : null}
        {p.adfDestino ? <KV label="Contrato novo" v={p.adfDestino} /> : null}
      </div>
      {prazo && portabilidadeEmAberto(p) ? <div style={{ marginTop: 10, fontSize: 12, color: "var(--gold-500)" }}>Etapa atual {prazo}.</div> : null}
      {p.motivo ? <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-muted)" }}>Motivo: {p.motivo}</div> : null}

      {p.estado === "aguardando_servidor" && !c && !p.fluxoLegado ? <p>Aguarde a instituição de destino apresentar as condições da proposta.</p> : null}
      {acoes.confirmar && (c || p.fluxoLegado) ? (
        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          <p style={{ margin: 0, fontSize: ".9rem" }}>
            O <b>{p.proponente.nome}</b> quer trazer o seu contrato {p.origem.adf}. Nada acontece sem a sua confirmação.
          </p>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: ".9rem" }}>
            <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} disabled={impersonando} />
            {p.fluxoLegado ? `Confirmo o pedido de portabilidade para o ${p.proponente.nome}, iniciado antes da atualização.` : `Li e aceito as condições da proposta acima para portar este contrato para o ${p.proponente.nome}.`}
          </label>
          {exigeSenha ? (
            <input
              style={{ ...inputStyle, maxWidth: 280 }}
              type="password"
              autoComplete="one-time-code"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Senha de transação"
              aria-label="Senha de transação"
            />
          ) : null}
        </div>
      ) : null}

      {erro ? (
        <div role="alert" style={{ marginTop: 10, color: "var(--danger, #DC2626)", fontSize: ".9rem" }}>
          {mensagemDeErro(erro, "Não foi possível concluir.")}
        </div>
      ) : null}

      {acoes.confirmar || acoes.cancelar ? (
        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {acoes.confirmar ? (
            <Button onClick={() => confirmar.mutate()} disabled={ocupado || impersonando || (!c && !p.fluxoLegado) || !ciente || (exigeSenha && !senha)}>
              {confirmar.isPending ? "Confirmando…" : "Confirmar portabilidade"}
            </Button>
          ) : null}
          {acoes.cancelar ? (
            <Button variant="ghost" onClick={() => cancelar.mutate()} disabled={ocupado || impersonando}>
              {acoes.confirmar ? "Recusar" : "Cancelar"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</span>
      {children}
    </label>
  );
}

function KV({ label, v, accent }: { label: string; v: string; accent?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, color: accent ? "var(--emerald-500)" : "var(--text)", fontWeight: accent ? 700 : 500 }}>{v}</div>
    </div>
  );
}
